import React from 'react';
import {
    Bold, Italic, Underline as UnderlineIcon, RemoveFormatting,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Table, Undo2, Redo2, MoreHorizontal
} from 'lucide-react';
import EditorToolbarButton from './EditorToolbarButton';
import HeadingDropdown from './components/HeadingDropdown';
import LinkDropdown from './components/LinkDropdown';
import QuickInsertDropdown from './components/QuickInsertDropdown';
import AiActionsDropdown from './components/AiActionsDropdown';
import { AddDropdown, DeleteDropdown } from './components/TableDropdowns';
import { BulletListDropdown, OrderedListDropdown } from './components/ListDropdowns';
import ImageDropdown from './components/ImageDropdown';
import TableBgDropdown from './components/TableBgDropdown';

const EditorFormattingToolbar = ({
    editor,
    editorRef,
    isTableActive,
    handleFormatAction,
    undo,
    redo,
    rfpId,
    isFullScreen,
    setIsFullScreen
}) => {
    const activeEditor = editorRef?.current || editor;
    const [showMore, setShowMore] = React.useState(false);
    const toolbarRef = React.useRef(null);
    const [containerWidth, setContainerWidth] = React.useState(1000);

    React.useEffect(() => {
        if (!toolbarRef.current) return;
        const observer = new ResizeObserver((entries) => {
            if (entries[0]) {
                setContainerWidth(entries[0].contentRect.width);
            }
        });
        observer.observe(toolbarRef.current);
        return () => observer.disconnect();
    }, []);

    // Smart breakpoints based on measured toolbar container width
    const showAllInline = containerWidth >= 960;
    const showAlignmentsInline = containerWidth >= 720;
    const showThreeDots = !showAllInline;

    return (
        <div ref={toolbarRef} className="editor-formatting-toolbar" style={{ flexWrap: 'nowrap', overflow: 'visible' }}>
            {/* Group 1: Undo / Redo */}
            <div className="toolbar-group">
                <EditorToolbarButton icon={Undo2} label="Undo" tooltip="Undo last edit" onClick={undo} />
                <EditorToolbarButton icon={Redo2} label="Redo" tooltip="Redo last edit" onClick={redo} />
            </div>

            <div className="toolbar-divider" />

            {/* Group 2: Document Structure */}
            <div className="toolbar-group">
                <HeadingDropdown editor={activeEditor} />
            </div>

            <div className="toolbar-divider" />

            {/* Group 3: Basic Text Formatting */}
            <div className="toolbar-group">
                <EditorToolbarButton icon={Bold} label="Bold" tooltip="Bold (Ctrl+B)" active={activeEditor?.isActive('bold')} onClick={() => handleFormatAction('Bold')} />
                <EditorToolbarButton icon={Italic} label="Italic" tooltip="Italic (Ctrl+I)" active={activeEditor?.isActive('italic')} onClick={() => handleFormatAction('Italic')} />
                <EditorToolbarButton icon={UnderlineIcon} label="Underline" tooltip="Underline (Ctrl+U)" active={activeEditor?.isActive('underline')} onClick={() => handleFormatAction('Underline')} />
                <div style={{ width: '1px', height: '14px', background: '#E2E8F0', alignSelf: 'center', margin: '0 2px' }} />
                <EditorToolbarButton icon={RemoveFormatting} label="Clear Format" tooltip="Clear Formatting" onClick={() => activeEditor?.chain().focus().unsetAllMarks().run()} />
            </div>

            <div className="toolbar-divider" />

            {/* Group 4: Lists */}
            <div className="toolbar-group" style={{ gap: '0.25rem' }}>
                <BulletListDropdown editor={activeEditor} handleFormatAction={handleFormatAction} />
                <OrderedListDropdown editor={activeEditor} handleFormatAction={handleFormatAction} />
            </div>

            {/* Group 5: Text Alignment (inline when width allows) */}
            {showAlignmentsInline && (
                <>
                    <div className="toolbar-divider" />
                    <div className="toolbar-group">
                        <EditorToolbarButton icon={AlignLeft} label="Align Left" tooltip="Align Left" active={activeEditor?.isActive({ textAlign: 'left' })} onClick={() => activeEditor?.chain().focus().setTextAlign('left').run()} />
                        <EditorToolbarButton icon={AlignCenter} label="Align Center" tooltip="Align Center" active={activeEditor?.isActive({ textAlign: 'center' })} onClick={() => activeEditor?.chain().focus().setTextAlign('center').run()} />
                        <EditorToolbarButton icon={AlignRight} label="Align Right" tooltip="Align Right" active={activeEditor?.isActive({ textAlign: 'right' })} onClick={() => activeEditor?.chain().focus().setTextAlign('right').run()} />
                        <EditorToolbarButton icon={AlignJustify} label="Justify" tooltip="Justify" active={activeEditor?.isActive({ textAlign: 'justify' })} onClick={() => activeEditor?.chain().focus().setTextAlign('justify').run()} />
                    </div>
                </>
            )}

            {/* Group 6 & 7: Table, Image & Link (inline when full width allows) */}
            {showAllInline && (
                <>
                    <div className="toolbar-divider" />
                    <div className="toolbar-group" style={{ gap: '0.25rem' }}>
                        <EditorToolbarButton icon={Table} label="Insert Table" tooltip="Insert Table" onClick={() => handleFormatAction('insertTable')} />
                        <AddDropdown isTableActive={isTableActive} handleFormatAction={handleFormatAction} />
                        <DeleteDropdown isTableActive={isTableActive} handleFormatAction={handleFormatAction} />
                        {isTableActive && (
                            <>
                                <div style={{ width: '1px', height: '16px', background: '#E2E8F0', alignSelf: 'center', margin: '0 2px' }} />
                                <button className="toolbar-btn" onClick={() => activeEditor?.chain().focus().mergeCells().run()} title="Merge Cells" style={{ fontSize: '11px', fontWeight: '700', padding: '0 6px' }}>Merge</button>
                                <button className="toolbar-btn" onClick={() => activeEditor?.chain().focus().splitCell().run()} title="Split Cells" style={{ fontSize: '11px', fontWeight: '700', padding: '0 6px' }}>Split</button>
                            </>
                        )}
                    </div>
                    <div className="toolbar-divider" />
                    <div className="toolbar-group" style={{ gap: '0.25rem' }}>
                        <ImageDropdown editor={activeEditor} editorRef={editorRef} rfpId={rfpId} />
                        <LinkDropdown editor={activeEditor} />
                    </div>
                </>
            )}

            {/* Three Dots More Dropdown Trigger */}
            {showThreeDots && (
                <div className="toolbar-group" style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowMore(!showMore)}
                        className={`toolbar-btn ${showMore ? 'is-active' : ''}`}
                        title="More formatting tools"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            background: showMore ? '#F1F5F9' : 'transparent',
                            border: '1px solid #E2E8F0',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            color: showMore ? '#FF5526' : '#64748B'
                        }}
                    >
                        <MoreHorizontal size={16} />
                    </button>

                    {showMore && (
                        <>
                            {/* Overlay backdrop to close when clicked outside */}
                            <div
                                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 }}
                                onClick={() => setShowMore(false)}
                            />
                            <div
                                className="toolbar-more-dropdown"
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 'calc(100% + 6px)',
                                    background: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '6px',
                                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                    padding: '0.5rem 0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    zIndex: 100,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {/* Sub-Group A: Alignments inside dropdown */}
                                {!showAlignmentsInline && (
                                    <div className="toolbar-group">
                                        <EditorToolbarButton icon={AlignLeft} label="Align Left" tooltip="Align Left" active={activeEditor?.isActive({ textAlign: 'left' })} onClick={() => { activeEditor?.chain().focus().setTextAlign('left').run(); setShowMore(false); }} />
                                        <EditorToolbarButton icon={AlignCenter} label="Align Center" tooltip="Align Center" active={activeEditor?.isActive({ textAlign: 'center' })} onClick={() => { activeEditor?.chain().focus().setTextAlign('center').run(); setShowMore(false); }} />
                                        <EditorToolbarButton icon={AlignRight} label="Align Right" tooltip="Align Right" active={activeEditor?.isActive({ textAlign: 'right' })} onClick={() => { activeEditor?.chain().focus().setTextAlign('right').run(); setShowMore(false); }} />
                                        <EditorToolbarButton icon={AlignJustify} label="Justify" tooltip="Justify" active={activeEditor?.isActive({ textAlign: 'justify' })} onClick={() => { activeEditor?.chain().focus().setTextAlign('justify').run(); setShowMore(false); }} />
                                    </div>
                                )}

                                {!showAlignmentsInline && !showAllInline && (
                                    <div style={{ width: '1px', height: '1.25rem', background: '#E2E8F0', margin: '0 0.25rem' }} />
                                )}

                                {/* Sub-Group B: Table Controls inside dropdown */}
                                {!showAllInline && (
                                    <div className="toolbar-group" style={{ gap: '0.25rem' }}>
                                        <EditorToolbarButton icon={Table} label="Insert Table" tooltip="Insert Table" onClick={() => { handleFormatAction('insertTable'); setShowMore(false); }} />
                                        <AddDropdown isTableActive={isTableActive} handleFormatAction={(action) => { handleFormatAction(action); setShowMore(false); }} />
                                        <DeleteDropdown isTableActive={isTableActive} handleFormatAction={(action) => { handleFormatAction(action); setShowMore(false); }} />
                                        {isTableActive && (
                                            <>
                                                <div style={{ width: '1px', height: '16px', background: '#E2E8F0', alignSelf: 'center', margin: '0 2px' }} />
                                                <button className="toolbar-btn" onClick={() => { activeEditor?.chain().focus().mergeCells().run(); setShowMore(false); }} title="Merge Cells" style={{ fontSize: '11px', fontWeight: '700', padding: '0 6px' }}>Merge</button>
                                                <button className="toolbar-btn" onClick={() => { activeEditor?.chain().focus().splitCell().run(); setShowMore(false); }} title="Split Cells" style={{ fontSize: '11px', fontWeight: '700', padding: '0 6px' }}>Split</button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {!showAllInline && (
                                    <div style={{ width: '1px', height: '1.25rem', background: '#E2E8F0', margin: '0 0.25rem' }} />
                                )}

                                {/* Sub-Group C: Image & Link inside dropdown */}
                                {!showAllInline && (
                                    <div className="toolbar-group" style={{ gap: '0.25rem' }}>
                                        <ImageDropdown editor={activeEditor} editorRef={editorRef} rfpId={rfpId} />
                                        <LinkDropdown editor={activeEditor} />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default EditorFormattingToolbar;
